<template>
  <div class="commission-config">
    <div class="commission-header">
      <div>
        <span class="section-kicker">Configuracion</span>
        <h2>Motor de comisiones</h2>
        <p>
          Define perfil del vendedor, base comisionable, escalas, penalizaciones
          y reglas de rentabilidad antes de liquidar el periodo.
        </p>
      </div>
      <button class="btn btn-primary btn-compact" @click="abrirModalVendedor(null)">
        <i class="bi bi-plus-circle me-1"></i>
        Nueva regla
      </button>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-3 col-6">
        <div class="metric-card">
          <span>Reglas activas</span>
          <strong>{{ totalActivas }}</strong>
          <small>{{ configuraciones.length }} configuracion(es)</small>
        </div>
      </div>
      <div class="col-md-3 col-6">
        <div class="metric-card">
          <span>Vendedores externos</span>
          <strong>{{ totalExternos }}</strong>
          <small>Meta sugerida 2.5x costo</small>
        </div>
      </div>
      <div class="col-md-3 col-6">
        <div class="metric-card">
          <span>Con cartera bloqueante</span>
          <strong>{{ totalBloqueoCartera }}</strong>
          <small>Control de flujo de caja</small>
        </div>
      </div>
      <div class="col-md-3 col-6">
        <div class="metric-card">
          <span>Base por recaudacion</span>
          <strong>{{ totalRecaudacion }}</strong>
          <small>Solo facturas pagadas</small>
        </div>
      </div>
    </div>

    <div v-if="mensajeError" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="bi bi-x-circle-fill"></i>
      <span>{{ mensajeError }}</span>
      <button type="button" class="btn-close ms-auto" @click="mensajeError = ''"></button>
    </div>
    <div v-if="mensajeExito" class="alert alert-success d-flex align-items-center gap-2">
      <i class="bi bi-check-circle-fill"></i>
      <span>{{ mensajeExito }}</span>
      <button type="button" class="btn-close ms-auto" @click="mensajeExito = ''"></button>
    </div>

    <div class="rules-panel">
      <div class="panel-title">
        <div>
          <span class="section-kicker">Matriz comercial</span>
          <h5>Reglas por vendedor</h5>
        </div>
        <button class="btn btn-outline-secondary btn-sm" @click="cargarConfiguraciones" :disabled="cargando">
          <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-clockwise me-1"></i>
          Recargar
        </button>
      </div>

      <div v-if="cargando" class="empty-state">
        <span class="spinner-border text-primary"></span>
        <p>Cargando reglas de comision...</p>
      </div>

      <div v-else-if="configuraciones.length === 0" class="empty-state">
        <i class="bi bi-cash-coin"></i>
        <p>No hay reglas de comision configuradas.</p>
        <button class="btn btn-primary btn-sm" @click="abrirModalVendedor(null)">
          Crear primera regla
        </button>
      </div>

      <div v-else class="seller-grid">
        <article v-for="cfg in configuraciones" :key="cfg.id" class="seller-card">
          <div class="seller-card__top">
            <div>
              <h6>{{ cfg.nombre_vendedor }}</h6>
              <small>{{ cfg.correo_vendedor }}</small>
            </div>
            <span :class="['status-pill', cfg.activo ? 'is-active' : 'is-muted']">
              {{ cfg.activo ? 'Activo' : 'Inactivo' }}
            </span>
          </div>

          <div class="seller-tags">
            <span>{{ etiquetaNaturaleza(cfg.naturaleza_vendedor) }}</span>
            <span>{{ etiquetaContratacion(cfg.tipo_contratacion) }}</span>
            <span>{{ etiquetaMetodo(cfg.metodo_calculo) }}</span>
          </div>

          <div class="seller-values">
            <div>
              <span>Meta mensual</span>
              <strong>{{ formatearMoneda(cfg.meta_base_mensual) }}</strong>
            </div>
            <div>
              <span>Costo total</span>
              <strong>{{ formatearMoneda(cfg.costo_total_mensual || cfg.sueldo_base) }}</strong>
            </div>
            <div>
              <span>Rangos</span>
              <strong>{{ cfg.num_rangos ?? (cfg.rangos?.length || 0) }}</strong>
            </div>
          </div>

          <div class="base-list">
            <span v-if="toBool(cfg.base_venta_neta)">Venta neta</span>
            <span v-if="toBool(cfg.base_recaudacion_efectiva)">Recaudacion</span>
            <span v-if="toBool(cfg.base_margen_utilidad)">Margen</span>
            <span v-if="toBool(cfg.base_categoria_producto)">Categoria</span>
          </div>

          <div class="seller-card__footer">
            <div :class="['profit-note', rentabilidadConfig(cfg).ok ? 'is-ok' : 'is-risk']">
              {{ rentabilidadConfig(cfg).texto }}
            </div>
            <button class="btn btn-outline-primary btn-sm" @click="abrirModalVendedor(cfg.usuario_id)">
              <i class="bi bi-sliders me-1"></i>
              Editar
            </button>
          </div>
        </article>
      </div>
    </div>

    <div v-if="modalVisible" class="modal-overlay" @click.self="cerrarModal">
      <div class="modal-contenedor">
        <div class="modal-cabecera">
          <div>
            <span class="section-kicker">{{ modoEdicion ? 'Editar regla' : 'Nueva regla' }}</span>
            <h5 class="mb-0">Perfil y matriz de comision</h5>
          </div>
          <button class="btn-close" @click="cerrarModal"></button>
        </div>

        <div class="modal-cuerpo">
          <div class="row g-3">
            <div class="col-xl-4">
              <section class="form-section">
                <h6><i class="bi bi-person-badge me-2"></i>Perfil del vendedor</h6>

                <div v-if="!modoEdicion" class="mb-3">
                  <label class="form-label">Vendedor *</label>
                  <select class="form-select" v-model="formulario.usuario_id">
                    <option value="">Selecciona un vendedor...</option>
                    <option v-for="v in vendedoresDisponibles" :key="v.id" :value="v.id">
                      {{ v.nombre_completo || v.nombres_apellidos || v.nombre }}
                    </option>
                  </select>
                </div>
                <div v-else class="mb-3">
                  <label class="form-label">Vendedor</label>
                  <input type="text" class="form-control" :value="nombreVendedorSeleccionado" readonly>
                </div>

                <label class="form-label">Naturaleza del vinculo</label>
                <div class="choice-stack mb-3">
                  <label :class="['choice-card', formulario.naturaleza_vendedor === 'interno' ? 'selected' : '']">
                    <input type="radio" value="interno" v-model="formulario.naturaleza_vendedor">
                    <i class="bi bi-shop"></i>
                    <span>
                      <strong>Interno</strong>
                      <small>Local, mostrador o venta pasiva.</small>
                    </span>
                  </label>
                  <label :class="['choice-card', formulario.naturaleza_vendedor === 'externo' ? 'selected' : '']">
                    <input type="radio" value="externo" v-model="formulario.naturaleza_vendedor">
                    <i class="bi bi-geo-alt"></i>
                    <span>
                      <strong>Externo</strong>
                      <small>Campo, ruta, visitas y prospeccion.</small>
                    </span>
                  </label>
                </div>

                <label class="form-label">Tipo de contratacion</label>
                <div class="segmented mb-3">
                  <button :class="{ active: formulario.tipo_contratacion === 'nomina' }"
                          @click="formulario.tipo_contratacion = 'nomina'">
                    Nomina
                  </button>
                  <button :class="{ active: formulario.tipo_contratacion === 'facturacion' }"
                          @click="formulario.tipo_contratacion = 'facturacion'">
                    Facturacion
                  </button>
                </div>

                <div class="row g-2">
                  <div class="col-6">
                    <label class="form-label">Sueldo base</label>
                    <div class="input-group">
                      <span class="input-group-text">$</span>
                      <input type="number" class="form-control" v-model.number="formulario.sueldo_base" min="0" step="0.01">
                    </div>
                  </div>
                  <div class="col-6">
                    <label class="form-label">Costo total</label>
                    <div class="input-group">
                      <span class="input-group-text">$</span>
                      <input type="number" class="form-control" v-model.number="formulario.costo_total_mensual" min="0" step="0.01">
                    </div>
                  </div>
                  <div class="col-12">
                    <label class="form-label">Meta mensual</label>
                    <div class="input-group">
                      <span class="input-group-text">$</span>
                      <input type="number" class="form-control" v-model.number="formulario.meta_base_mensual" min="0" step="0.01">
                    </div>
                  </div>
                </div>

                <div :class="['rentability-box', rentabilidadFormulario.ok ? 'is-ok' : 'is-risk']">
                  <strong>{{ rentabilidadFormulario.titulo }}</strong>
                  <span>{{ rentabilidadFormulario.texto }}</span>
                </div>
              </section>
            </div>

            <div class="col-xl-8">
              <section class="form-section">
                <h6><i class="bi bi-bullseye me-2"></i>Base comisionable</h6>
                <div class="base-selector">
                  <label v-for="base in basesDisponibles" :key="base.key"
                         :class="['base-option', formulario[base.key] ? 'selected' : '']">
                    <input type="checkbox" v-model="formulario[base.key]">
                    <i :class="base.icon"></i>
                    <span>
                      <strong>{{ base.label }}</strong>
                      <small>{{ base.help }}</small>
                    </span>
                  </label>
                </div>
              </section>

              <section class="form-section">
                <h6><i class="bi bi-diagram-3 me-2"></i>Metodo de calculo</h6>
                <div class="method-grid">
                  <label v-for="metodo in metodos" :key="metodo.key"
                         :class="['method-card', formulario.metodo_calculo === metodo.key ? 'selected' : '']">
                    <input type="radio" :value="metodo.key" v-model="formulario.metodo_calculo">
                    <i :class="metodo.icon"></i>
                    <span>
                      <strong>{{ metodo.label }}</strong>
                      <small>{{ metodo.help }}</small>
                    </span>
                  </label>
                </div>
              </section>

              <section v-if="formulario.metodo_calculo !== 'cumplimiento_meta'" class="form-section">
                <div class="section-toolbar">
                  <h6><i class="bi bi-bar-chart-steps me-2"></i>Escalas progresivas por monto</h6>
                  <button class="btn btn-outline-success btn-sm" @click="agregarRango">
                    <i class="bi bi-plus"></i>
                    Agregar
                  </button>
                </div>

                <div v-for="(rango, idx) in formulario.rangos" :key="`monto-${idx}`" class="rule-row">
                  <span class="rule-index">{{ idx + 1 }}</span>
                  <div>
                    <label>Desde $</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="rango.desde_monto" min="0" step="0.01">
                  </div>
                  <div>
                    <label>Hasta $</label>
                    <input type="number" class="form-control form-control-sm" v-model="rango.hasta_monto" min="0" step="0.01" placeholder="Sin limite">
                  </div>
                  <div>
                    <label>%</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="rango.porcentaje_comision" min="0" max="100" step="0.1">
                  </div>
                  <div>
                    <label>Bono $</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="rango.bono_adicional" min="0" step="0.01">
                  </div>
                  <button class="btn btn-outline-danger btn-sm icon-btn" @click="eliminarRango(idx)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </section>

              <section v-if="formulario.metodo_calculo !== 'progresivo_monto'" class="form-section">
                <div class="section-toolbar">
                  <h6><i class="bi bi-speedometer2 me-2"></i>Escalas por cumplimiento de meta</h6>
                  <button class="btn btn-outline-success btn-sm" @click="agregarRangoCumplimiento">
                    <i class="bi bi-plus"></i>
                    Agregar
                  </button>
                </div>

                <div v-for="(rango, idx) in formulario.reglas_cumplimiento" :key="`cumplimiento-${idx}`" class="rule-row">
                  <span class="rule-index">{{ idx + 1 }}</span>
                  <div>
                    <label>Desde %</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="rango.desde_pct" min="0" step="0.1">
                  </div>
                  <div>
                    <label>Hasta %</label>
                    <input type="number" class="form-control form-control-sm" v-model="rango.hasta_pct" min="0" step="0.1" placeholder="Sin limite">
                  </div>
                  <div>
                    <label>% comision</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="rango.porcentaje_comision" min="0" max="100" step="0.1">
                  </div>
                  <div>
                    <label>Bono $</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="rango.bono_adicional" min="0" step="0.01">
                  </div>
                  <button class="btn btn-outline-danger btn-sm icon-btn" @click="eliminarRangoCumplimiento(idx)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </section>

              <section v-if="formulario.base_categoria_producto" class="form-section">
                <div class="section-toolbar">
                  <h6><i class="bi bi-tags me-2"></i>Por categoria de producto</h6>
                  <button class="btn btn-outline-success btn-sm" @click="agregarReglaCategoria">
                    <i class="bi bi-plus"></i>
                    Agregar
                  </button>
                </div>

                <div v-if="categorias.length === 0" class="helper-note">
                  No se pudieron cargar categorias. La regla se puede dejar lista y ajustar despues.
                </div>

                <div v-for="(regla, idx) in formulario.reglas_categoria" :key="`cat-${idx}`" class="category-row">
                  <select class="form-select form-select-sm" v-model="regla.categoria_id">
                    <option value="">Selecciona categoria...</option>
                    <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
                      {{ cat.categoria || cat.nombre }}
                    </option>
                  </select>
                  <div class="input-group input-group-sm">
                    <input type="number" class="form-control" v-model.number="regla.porcentaje_comision" min="0" max="100" step="0.1">
                    <span class="input-group-text">%</span>
                  </div>
                  <button class="btn btn-outline-danger btn-sm icon-btn" @click="eliminarReglaCategoria(idx)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </section>

              <section class="form-section">
                <h6><i class="bi bi-shield-exclamation me-2"></i>Aceleradores y penalizaciones</h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="switch-line">
                      <input type="checkbox" v-model="formulario.penalizar_descuento">
                      <span>
                        <strong>Penalizar descuento alto</strong>
                        <small>Si supera el maximo, cae al rango minimo.</small>
                      </span>
                    </label>
                    <div class="input-group mt-2">
                      <input type="number" class="form-control" v-model.number="formulario.descuento_maximo_pct" min="0" max="100" step="0.1">
                      <span class="input-group-text">% max.</span>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="switch-line">
                      <input type="checkbox" v-model="formulario.bloquear_cartera_vencida">
                      <span>
                        <strong>Bloquear por cartera</strong>
                        <small>Retiene comision con facturas vencidas.</small>
                      </span>
                    </label>
                    <div class="input-group mt-2">
                      <input type="number" class="form-control" v-model.number="formulario.dias_cartera_bloqueo" min="1" step="1">
                      <span class="input-group-text">dias</span>
                    </div>
                  </div>
                </div>
              </section>

              <section class="form-section compact-section">
                <label class="switch-line mb-0">
                  <input type="checkbox" v-model="formulario.activo">
                  <span>
                    <strong>Regla activa</strong>
                    <small>Los meses liquidados quedan protegidos por auditoria y aprobacion.</small>
                  </span>
                </label>
              </section>
            </div>
          </div>
        </div>

        <div class="modal-pie">
          <button class="btn btn-secondary" @click="cerrarModal" :disabled="guardando">Cancelar</button>
          <button class="btn btn-primary" @click="guardar" :disabled="guardando">
            <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-floppy me-1"></i>
            {{ guardando ? 'Guardando...' : 'Guardar regla' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { comisionesService } from '@/services/comisionesService'
import api from '@/services/api'

export default {
  name: 'ClientComisionesConfiguracion',

  data () {
    return {
      cargando: false,
      guardando: false,
      mensajeError: '',
      mensajeExito: '',
      configuraciones: [],
      vendedoresDisponibles: [],
      categorias: [],
      modalVisible: false,
      modoEdicion: false,
      formulario: this.formularioVacio(),
      basesDisponibles: [
        {
          key: 'base_venta_neta',
          label: 'Venta neta',
          help: 'Subtotal sin IVA, retenciones ni fletes.',
          icon: 'bi bi-receipt'
        },
        {
          key: 'base_recaudacion_efectiva',
          label: 'Recaudacion efectiva',
          help: 'Comisiona solo sobre facturas pagadas.',
          icon: 'bi bi-bank'
        },
        {
          key: 'base_margen_utilidad',
          label: 'Margen de utilidad',
          help: 'Base sobre ganancia real venta menos costo.',
          icon: 'bi bi-graph-up-arrow'
        },
        {
          key: 'base_categoria_producto',
          label: 'Categoria de producto',
          help: 'Porcentajes diferentes por linea comercial.',
          icon: 'bi bi-tags'
        }
      ],
      metodos: [
        {
          key: 'cumplimiento_meta',
          label: 'Cumplimiento de meta',
          help: 'Castigo, minimo, intermedio y bono al 100%.',
          icon: 'bi bi-bullseye'
        },
        {
          key: 'progresivo_monto',
          label: 'Tiers por monto',
          help: 'Porcentaje segun volumen vendido del mes.',
          icon: 'bi bi-bar-chart-steps'
        },
        {
          key: 'mixto',
          label: 'Mixto',
          help: 'Combina cuota, monto, categorias y controles.',
          icon: 'bi bi-diagram-3'
        }
      ]
    }
  },

  computed: {
    totalActivas () {
      return this.configuraciones.filter(c => this.toBool(c.activo)).length
    },
    totalExternos () {
      return this.configuraciones.filter(c => c.naturaleza_vendedor === 'externo').length
    },
    totalBloqueoCartera () {
      return this.configuraciones.filter(c => this.toBool(c.bloquear_cartera_vencida)).length
    },
    totalRecaudacion () {
      return this.configuraciones.filter(c => this.toBool(c.base_recaudacion_efectiva)).length
    },
    nombreVendedorSeleccionado () {
      const vendedor = this.vendedoresDisponibles.find(x => Number(x.id) === Number(this.formulario.usuario_id))
      return vendedor ? (vendedor.nombre_completo || vendedor.nombres_apellidos || vendedor.nombre) : ''
    },
    rentabilidadFormulario () {
      const costo = Number(this.formulario.costo_total_mensual || this.formulario.sueldo_base || 0)
      const meta = Number(this.formulario.meta_base_mensual || 0)
      if (this.formulario.naturaleza_vendedor !== 'externo' || costo <= 0) {
        return { ok: true, titulo: 'Control operativo', texto: 'Para mostrador, la meta puede basarse en trafico del local.' }
      }
      const sugerida = costo * 2.5
      const ok = meta >= sugerida
      return {
        ok,
        titulo: ok ? 'Meta rentable' : 'Meta por debajo de 2.5x',
        texto: `Sugerido para campo: ${this.formatearMoneda(sugerida)}.`
      }
    }
  },

  mounted () {
    this.cargarConfiguraciones()
    this.cargarVendedores()
    this.cargarCategorias()
  },

  methods: {
    formularioVacio () {
      return {
        usuario_id: '',
        naturaleza_vendedor: 'interno',
        tipo_contratacion: 'nomina',
        sueldo_base: 0,
        costo_total_mensual: 0,
        meta_base_mensual: 10000,
        metodo_calculo: 'progresivo_monto',
        base_venta_neta: true,
        base_recaudacion_efectiva: true,
        base_margen_utilidad: false,
        base_categoria_producto: false,
        descuento_maximo_pct: 15,
        penalizar_descuento: true,
        bloquear_cartera_vencida: true,
        dias_cartera_bloqueo: 60,
        activo: true,
        rangos: this.rangosMontoBase(),
        reglas_cumplimiento: this.rangosCumplimientoBase(),
        reglas_categoria: []
      }
    },
    rangosMontoBase () {
      return [
        { desde_monto: 0, hasta_monto: 5000, porcentaje_comision: 2, bono_adicional: 0 },
        { desde_monto: 5001, hasta_monto: 15000, porcentaje_comision: 3.5, bono_adicional: 0 },
        { desde_monto: 15001, hasta_monto: '', porcentaje_comision: 5, bono_adicional: 0 }
      ]
    },
    rangosCumplimientoBase () {
      return [
        { desde_pct: 0, hasta_pct: 50, porcentaje_comision: 0, bono_adicional: 0 },
        { desde_pct: 51, hasta_pct: 70, porcentaje_comision: 2, bono_adicional: 0 },
        { desde_pct: 71, hasta_pct: 99, porcentaje_comision: 3.5, bono_adicional: 0 },
        { desde_pct: 100, hasta_pct: '', porcentaje_comision: 5, bono_adicional: 0 }
      ]
    },
    async cargarConfiguraciones () {
      this.cargando = true
      this.mensajeError = ''
      try {
        const resp = await comisionesService.listarConfiguraciones()
        this.configuraciones = resp.data || []
      } catch (e) {
        this.mensajeError = e?.response?.data?.error || 'Error al cargar las reglas de comision.'
      } finally {
        this.cargando = false
      }
    },
    async cargarVendedores () {
      try {
        const resp = await api.get('/configuracion/vendedores')
        this.vendedoresDisponibles = resp.data?.data || []
      } catch {
        this.vendedoresDisponibles = []
      }
    },
    async cargarCategorias () {
      try {
        const resp = await api.get('/categorias')
        this.categorias = resp.data?.data || resp.data || []
      } catch {
        this.categorias = []
      }
    },
    async abrirModalVendedor (usuarioId) {
      this.mensajeError = ''
      this.mensajeExito = ''
      this.formulario = this.formularioVacio()
      this.modoEdicion = !!usuarioId

      if (usuarioId) {
        try {
          const resp = await comisionesService.getConfiguracion(usuarioId)
          const cfg = resp.data
          if (cfg) this.cargarFormulario(cfg)
        } catch (e) {
          this.mensajeError = e?.response?.data?.error || 'Error al cargar la regla.'
          return
        }
      }

      this.modalVisible = true
    },
    cargarFormulario (cfg) {
      this.formulario = {
        ...this.formularioVacio(),
        usuario_id: cfg.usuario_id,
        naturaleza_vendedor: cfg.naturaleza_vendedor || 'interno',
        tipo_contratacion: cfg.tipo_contratacion || 'nomina',
        sueldo_base: Number(cfg.sueldo_base || 0),
        costo_total_mensual: Number(cfg.costo_total_mensual || cfg.sueldo_base || 0),
        meta_base_mensual: Number(cfg.meta_base_mensual || 0),
        metodo_calculo: cfg.metodo_calculo || 'progresivo_monto',
        base_venta_neta: this.toBool(cfg.base_venta_neta),
        base_recaudacion_efectiva: this.toBool(cfg.base_recaudacion_efectiva),
        base_margen_utilidad: this.toBool(cfg.base_margen_utilidad),
        base_categoria_producto: this.toBool(cfg.base_categoria_producto),
        descuento_maximo_pct: Number(cfg.descuento_maximo_pct || 0),
        penalizar_descuento: this.toBool(cfg.penalizar_descuento),
        bloquear_cartera_vencida: this.toBool(cfg.bloquear_cartera_vencida),
        dias_cartera_bloqueo: Number(cfg.dias_cartera_bloqueo || 60),
        activo: this.toBool(cfg.activo),
        rangos: (cfg.rangos && cfg.rangos.length ? cfg.rangos : this.rangosMontoBase()).map(r => ({
          desde_monto: Number(r.desde_monto || 0),
          hasta_monto: r.hasta_monto !== null && r.hasta_monto !== undefined ? r.hasta_monto : '',
          porcentaje_comision: Number(r.porcentaje_comision || 0),
          bono_adicional: Number(r.bono_adicional || 0)
        })),
        reglas_cumplimiento: this.normalizarJson(cfg.reglas_cumplimiento || cfg.reglas_cumplimiento_json, this.rangosCumplimientoBase()),
        reglas_categoria: this.normalizarJson(cfg.reglas_categoria || cfg.reglas_categoria_json, [])
      }
    },
    cerrarModal () {
      this.modalVisible = false
      this.formulario = this.formularioVacio()
    },
    agregarRango () {
      this.formulario.rangos.push({ desde_monto: 0, hasta_monto: '', porcentaje_comision: 0, bono_adicional: 0 })
    },
    eliminarRango (idx) {
      this.formulario.rangos.splice(idx, 1)
    },
    agregarRangoCumplimiento () {
      this.formulario.reglas_cumplimiento.push({ desde_pct: 0, hasta_pct: '', porcentaje_comision: 0, bono_adicional: 0 })
    },
    eliminarRangoCumplimiento (idx) {
      this.formulario.reglas_cumplimiento.splice(idx, 1)
    },
    agregarReglaCategoria () {
      this.formulario.reglas_categoria.push({ categoria_id: '', porcentaje_comision: 0 })
    },
    eliminarReglaCategoria (idx) {
      this.formulario.reglas_categoria.splice(idx, 1)
    },
    async guardar () {
      if (!this.formulario.usuario_id) {
        this.mensajeError = 'Debes seleccionar un vendedor.'
        return
      }
      if (!this.formulario.base_venta_neta && !this.formulario.base_recaudacion_efectiva && !this.formulario.base_margen_utilidad && !this.formulario.base_categoria_producto) {
        this.mensajeError = 'Selecciona al menos una base comisionable.'
        return
      }

      this.guardando = true
      this.mensajeError = ''
      const datos = {
        naturaleza_vendedor: this.formulario.naturaleza_vendedor,
        tipo_contratacion: this.formulario.tipo_contratacion,
        sueldo_base: this.formulario.sueldo_base,
        costo_total_mensual: this.formulario.costo_total_mensual,
        meta_base_mensual: this.formulario.meta_base_mensual,
        metodo_calculo: this.formulario.metodo_calculo,
        base_venta_neta: this.formulario.base_venta_neta ? 1 : 0,
        base_recaudacion_efectiva: this.formulario.base_recaudacion_efectiva ? 1 : 0,
        base_margen_utilidad: this.formulario.base_margen_utilidad ? 1 : 0,
        base_categoria_producto: this.formulario.base_categoria_producto ? 1 : 0,
        descuento_maximo_pct: this.formulario.descuento_maximo_pct,
        penalizar_descuento: this.formulario.penalizar_descuento ? 1 : 0,
        bloquear_cartera_vencida: this.formulario.bloquear_cartera_vencida ? 1 : 0,
        dias_cartera_bloqueo: this.formulario.dias_cartera_bloqueo,
        activo: this.formulario.activo ? 1 : 0,
        rangos: this.formulario.rangos.map(r => ({
          desde_monto: r.desde_monto,
          hasta_monto: r.hasta_monto !== '' && r.hasta_monto !== null ? r.hasta_monto : null,
          porcentaje_comision: r.porcentaje_comision,
          bono_adicional: r.bono_adicional
        })),
        reglas_cumplimiento: this.formulario.reglas_cumplimiento,
        reglas_categoria: this.formulario.reglas_categoria
      }

      try {
        await comisionesService.guardarConfiguracion(this.formulario.usuario_id, datos)
        this.mensajeExito = 'Regla de comision guardada correctamente.'
        this.cerrarModal()
        await this.cargarConfiguraciones()
      } catch (e) {
        this.mensajeError = e?.response?.data?.error || 'Error al guardar la regla.'
      } finally {
        this.guardando = false
      }
    },
    rentabilidadConfig (cfg) {
      const costo = Number(cfg.costo_total_mensual || cfg.sueldo_base || 0)
      const meta = Number(cfg.meta_base_mensual || 0)
      if (cfg.naturaleza_vendedor !== 'externo' || costo <= 0) {
        return { ok: true, texto: 'Meta basada en trafico local' }
      }
      const ok = meta >= costo * 2.5
      return {
        ok,
        texto: ok ? 'Meta rentable para campo' : `Sugerido: ${this.formatearMoneda(costo * 2.5)}`
      }
    },
    normalizarJson (valor, fallback) {
      if (Array.isArray(valor)) return valor
      if (!valor) return fallback
      try {
        const parsed = JSON.parse(valor)
        return Array.isArray(parsed) ? parsed : fallback
      } catch {
        return fallback
      }
    },
    toBool (valor) {
      return valor === true || valor === 1 || valor === '1'
    },
    etiquetaNaturaleza (valor) {
      return valor === 'externo' ? 'Externo / Ruta' : 'Interno / Mostrador'
    },
    etiquetaContratacion (valor) {
      return valor === 'facturacion' ? 'Facturacion' : 'Nomina'
    },
    etiquetaMetodo (valor) {
      const metodo = this.metodos.find(m => m.key === valor)
      return metodo ? metodo.label : 'Tiers por monto'
    },
    formatearMoneda (valor) {
      return '$ ' + Number(valor || 0).toFixed(2)
    }
  }
}
</script>

<style scoped>
.commission-config {
  padding: 1.25rem;
  color: var(--text-primary, #1f2937);
}

.commission-header,
.panel-title,
.section-toolbar,
.seller-card__top,
.seller-card__footer,
.modal-cabecera,
.modal-pie {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.commission-header {
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.commission-header h2 {
  margin: 0;
  font-weight: 750;
  color: var(--text-primary, #162033);
}

.commission-header p {
  max-width: 760px;
  margin: .35rem 0 0;
  color: var(--text-muted, #6b7280);
}

.section-kicker {
  display: inline-block;
  margin-bottom: .25rem;
  color: var(--primary-color, #1173d4);
  font-size: .72rem;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.btn-compact {
  min-width: 138px;
}

.metric-card,
.rules-panel,
.form-section,
.seller-card {
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, .05);
}

.metric-card {
  min-height: 118px;
  padding: 1rem;
}

.metric-card span,
.seller-values span,
.rule-row label {
  display: block;
  color: var(--text-muted, #6b7280);
  font-size: .76rem;
  font-weight: 700;
}

.metric-card strong {
  display: block;
  margin-top: .3rem;
  font-size: 1.8rem;
  color: var(--primary-color, #1173d4);
}

.metric-card small {
  color: var(--text-muted, #6b7280);
}

.rules-panel {
  overflow: hidden;
}

.panel-title {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.panel-title h5,
.form-section h6 {
  margin: 0;
  font-weight: 750;
}

.empty-state {
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text-muted, #6b7280);
}

.empty-state i {
  display: block;
  margin-bottom: .75rem;
  font-size: 2.25rem;
  color: var(--primary-color, #1173d4);
}

.seller-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(310px, 1fr));
  gap: 1rem;
  padding: 1rem;
}

.seller-card {
  padding: 1rem;
}

.seller-card h6 {
  margin: 0;
  font-weight: 750;
}

.seller-card small {
  color: var(--text-muted, #6b7280);
}

.status-pill,
.seller-tags span,
.base-list span {
  border-radius: 999px;
  font-size: .72rem;
  font-weight: 800;
  padding: .28rem .6rem;
}

.status-pill.is-active {
  color: #146c43;
  background: rgba(25, 135, 84, .12);
}

.status-pill.is-muted {
  color: #64748b;
  background: rgba(100, 116, 139, .12);
}

.seller-tags,
.base-list {
  display: flex;
  gap: .4rem;
  flex-wrap: wrap;
  margin-top: .85rem;
}

.seller-tags span {
  background: rgba(17, 115, 212, .09);
  color: var(--primary-color, #1173d4);
}

.base-list span {
  background: rgba(15, 23, 42, .06);
  color: #334155;
}

.seller-values {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: .7rem;
  margin-top: 1rem;
}

.seller-values div {
  padding: .75rem;
  border-radius: 8px;
  background: var(--table-header-bg, #f8fafc);
}

.seller-values strong {
  display: block;
  margin-top: .25rem;
  font-size: .98rem;
}

.profit-note {
  font-size: .78rem;
  font-weight: 800;
}

.profit-note.is-ok {
  color: #146c43;
}

.profit-note.is-risk {
  color: #b45309;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, .55);
}

.modal-contenedor {
  width: min(1180px, 100%);
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  background: var(--card-bg, #fff);
  border-radius: 8px;
  box-shadow: 0 22px 70px rgba(0, 0, 0, .22);
}

.modal-cabecera,
.modal-pie {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.modal-pie {
  border-top: 1px solid var(--border-color, #e2e8f0);
  border-bottom: 0;
  justify-content: flex-end;
}

.modal-cuerpo {
  padding: 1.25rem;
  overflow-y: auto;
}

.form-section {
  padding: 1rem;
  margin-bottom: 1rem;
}

.compact-section {
  padding: .85rem 1rem;
}

.choice-stack,
.base-selector,
.method-grid {
  display: grid;
  gap: .65rem;
}

.base-selector {
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
}

.method-grid {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.choice-card,
.base-option,
.method-card,
.switch-line {
  display: flex;
  gap: .75rem;
  align-items: flex-start;
  padding: .85rem;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  background: var(--table-header-bg, #f8fafc);
  cursor: pointer;
}

.choice-card.selected,
.base-option.selected,
.method-card.selected {
  border-color: var(--primary-color, #1173d4);
  background: rgba(17, 115, 212, .08);
}

.choice-card input,
.base-option input,
.method-card input {
  margin-top: .25rem;
}

.choice-card i,
.base-option i,
.method-card i {
  color: var(--primary-color, #1173d4);
  font-size: 1.2rem;
}

.choice-card strong,
.base-option strong,
.method-card strong,
.switch-line strong {
  display: block;
  line-height: 1.1;
}

.choice-card small,
.base-option small,
.method-card small,
.switch-line small {
  color: var(--text-muted, #6b7280);
}

.segmented {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: .25rem;
  background: var(--table-header-bg, #f1f5f9);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
}

.segmented button {
  border: 0;
  border-radius: 6px;
  padding: .55rem .75rem;
  background: transparent;
  color: var(--text-muted, #6b7280);
  font-weight: 800;
}

.segmented button.active {
  background: #fff;
  color: var(--primary-color, #1173d4);
  box-shadow: 0 4px 12px rgba(15, 23, 42, .08);
}

.rentability-box {
  margin-top: 1rem;
  padding: .85rem;
  border-radius: 8px;
  border: 1px solid transparent;
}

.rentability-box strong,
.rentability-box span {
  display: block;
}

.rentability-box.is-ok {
  color: #146c43;
  background: rgba(25, 135, 84, .09);
  border-color: rgba(25, 135, 84, .18);
}

.rentability-box.is-risk {
  color: #92400e;
  background: rgba(245, 158, 11, .13);
  border-color: rgba(245, 158, 11, .3);
}

.rule-row {
  display: grid;
  grid-template-columns: 32px repeat(4, 1fr) 38px;
  gap: .55rem;
  align-items: end;
  padding: .75rem;
  margin-top: .65rem;
  border-radius: 8px;
  background: var(--table-header-bg, #f8fafc);
}

.rule-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(17, 115, 212, .12);
  color: var(--primary-color, #1173d4);
  font-weight: 800;
}

.icon-btn {
  width: 34px;
  height: 31px;
  padding: 0;
}

.category-row {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 130px 38px;
  gap: .55rem;
  margin-top: .65rem;
}

.helper-note {
  padding: .75rem;
  color: #92400e;
  background: rgba(245, 158, 11, .13);
  border-radius: 8px;
}

@media (max-width: 768px) {
  .commission-config {
    padding: .85rem;
  }

  .seller-values,
  .rule-row,
  .category-row {
    grid-template-columns: 1fr;
  }

  .rule-index,
  .icon-btn {
    width: 100%;
    border-radius: 6px;
  }
}
</style>

